// Your space (wave 12, account track): the signed-in home. What changed for you, Continue playing, your library,
// the games you love and the creators you follow, and your Open Haunt pumpkin. Modules with nothing in them stay
// hidden; a brand-new account gets one clear invitation instead of a page of empty boxes.

import { api } from "../api.ts";
import { gameById, getCatalogue, type Catalogue, type Game } from "../catalogue.ts";
import { gameCard } from "../components.ts";
import { clear, errorBox, fmtPlaytime, h, link } from "../dom.ts";
import { progressText, questFrom, type QuestProgress } from "../haunt.ts";
import { navigate, setTitle } from "../router.ts";
import { coverUrl, inSeason } from "../rows.ts";
import { nameOf, signedIn } from "../session.ts";
import { accountStyles, getFullMe, needsFirstRun, type FullMe } from "./me-kit.ts";

export interface Notification {
  id: string;
  kind: string;
  gameId: string;
  data: Record<string, unknown>;
  createdAt: number;
  readAt: number | null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** One notification in plain words: what happened, and to what. Unknown kinds say nothing rather than guess. */
export function describe(n: Pick<Notification, "kind" | "data">, title: string): { line: string; detail: string | null } | null {
  if (n.kind === "creator_shipped") {
    const who = str(n.data.creatorName);
    const what = str(n.data.title) ?? title;
    return who && what ? { line: `${who} shipped ${what}`, detail: "A creator you follow has a new game." } : null;
  }
  if (n.kind === "event_starting") {
    const name = str(n.data.title);
    if (!name) return null;
    const at = typeof n.data.at === "number" ? new Date(n.data.at * 1000).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : null;
    return { line: `${name} is starting`, detail: at ? `Starts at ${at}. Come along.` : "Come along." };
  }
  if (n.kind === "game_released") return { line: `${title} is out`, detail: str(n.data.creatorName) ? `From ${str(n.data.creatorName)}, which you were waiting for.` : "A game you were waiting for." };
  if (n.kind === "game_updated") {
    const v = str(n.data.versionLabel);
    return { line: v ? `${title} updated to ${v}` : `${title} got an update`, detail: str(n.data.notes)?.slice(0, 160) ?? null };
  }
  return null;
}

/** "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", else the date. */
export function ago(unixSeconds: number, nowSec = Date.now() / 1000): string {
  const s = Math.max(0, nowSec - unixSeconds);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86400) return "yesterday";
  if (s < 14 * 86400) return `${Math.round(s / 86400)} days ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function strip(games: Game[], sub?: (g: Game) => string | null): HTMLElement {
  return h(
    "ul",
    { class: "acct-strip" },
    games.map((g) => {
      const card = gameCard(g);
      const extra = sub?.(g);
      if (extra) card.querySelector(".card-sub")!.textContent = extra;
      return card;
    }),
  );
}

function module(id: string, title: string, action: HTMLElement | null, ...body: (HTMLElement | null)[]): HTMLElement {
  return h("section", { id, class: "acct-mod acct-enter" }, h("div", { class: "acct-mod-head" }, h("h2", null, title), action), ...body);
}

export async function meHomePage(): Promise<HTMLElement> {
  setTitle("Your space");
  accountStyles();
  if (!signedIn()) {
    navigate(`/signin?next=${encodeURIComponent("/me")}`, true);
    return h("div");
  }
  const mods = h("div", { class: "acct-mods" });
  const hello = h("div", { class: "acct-hello" });
  const root = h("div", { class: "acct acct-page" }, hello, mods);

  let me: FullMe;
  let cat: Catalogue;
  try {
    [me, cat] = await Promise.all([getFullMe(), getCatalogue()]);
  } catch (e) {
    clear(mods, errorBox(e));
    return root;
  }
  if (needsFirstRun(me)) {
    navigate("/welcome", true);
    return h("div");
  }
  const [lib, recent, inbox, quest] = await Promise.all([
    api<{ library: string[]; hearts: string[]; follows: { games: string[]; creators: string[] } }>("accounts", "/me/library").catch(() => null),
    api<{ games: { gameId: string; lastPlayedAt: number; seconds: number }[] }>("accounts", "/me/recent").catch(() => null),
    api<{ unread: number; notifications: Notification[] }>("accounts", "/me/notifications?limit=20").catch(() => null),
    inSeason(cat) ? api<unknown>("accounts", "/me/unlocks").then(questFrom, () => null) : Promise.resolve(null),
  ]);

  const games = (ids: string[]) => ids.map((id) => gameById(cat, id)).filter((g): g is Game => !!g);
  const playing = (recent?.games ?? []).map((r) => ({ r, g: gameById(cat, r.gameId) })).filter((x): x is { r: typeof x.r; g: Game } => !!x.g);
  const library = games(lib?.library ?? []);
  const hearts = games(lib?.hearts ?? []);
  const creators = (lib?.follows.creators ?? []).map((id) => cat.creators.find((c) => c.id === id)).filter((c): c is Catalogue["creators"][number] => !!c);
  // every kind names a game except event_starting (rooms' events); a game kind whose game left the catalogue is skipped
  const notes = (inbox?.notifications ?? [])
    // event_starting carries the event id in gameId (accounts' one-per-subject index): never look it up as a game
    .map((n) => ({ n, g: n.kind !== "event_starting" && n.gameId ? gameById(cat, n.gameId) : undefined }))
    .filter((x) => (x.g || x.n.kind === "event_starting") && describe(x.n, x.g?.title ?? ""));

  // the welcome: who you are, and the one thing worth doing next
  const lead = playing[0]?.g ?? library[0] ?? hearts[0] ?? null;
  const bg = h("div", { class: "acct-hello-bg", "aria-hidden": "true" });
  if (lead && coverUrl(lead, "full")) bg.style.backgroundImage = `url("${coverUrl(lead, "full")}")`;
  clear(
    hello,
    bg,
    h(
      "div",
      { class: "acct-hello-in" },
      h("p", { class: "kicker" }, "Your space"),
      h("h1", null, `Hey, ${nameOf(me).replace(/^@/, "")}.`),
      h("p", { class: "lede" }, playing[0] ? `Last time you were playing ${playing[0].g.title}.` : inbox?.unread ? `${inbox.unread} new ${inbox.unread === 1 ? "thing" : "things"} since you were here.` : "Everything that's yours on Banger, in one place."),
      h(
        "div",
        { class: "acct-row" },
        playing[0] ? link(`/g/${playing[0].g.id}`, { class: "btn" }, `Back to ${playing[0].g.title}`) : link("/browse", { class: "btn" }, "Find something to play"),
        me.handle ? link(`/u/${me.handle}`, { class: "btn ghost" }, "Your profile") : null,
        link("/me/settings", { class: "btn ghost" }, "Settings"),
      ),
    ),
  );

  const blocks: (HTMLElement | null)[] = [];

  if (notes.length) {
    const unread = notes.filter((x) => !x.n.readAt).length;
    const readAll = unread
      ? h("button", { class: "acct-link", type: "button", onclick: async (e: Event) => {
          (e.currentTarget as HTMLElement).remove();
          await api("accounts", "/me/notifications/read", { body: { all: true } }).catch(() => {});
          root.querySelectorAll(".acct-note.unread").forEach((el) => el.classList.remove("unread"));
        } }, "Mark all read")
      : null;
    blocks.push(
      module(
        "inbox",
        unread ? `What's new · ${unread}` : "What's new",
        readAll,
        h(
          "ul",
          { class: "acct-notes" },
          notes.slice(0, 8).map(({ n, g }) => {
            const d = describe(n, g?.title ?? "")!;
            const art = h("span", { class: "acct-note-art", "aria-hidden": "true" });
            const c = g ? coverUrl(g, "thumb") : null;
            if (c) art.style.backgroundImage = `url("${c}")`;
            return h("li", { class: `acct-note${n.readAt ? "" : " unread"}` }, link(g ? `/g/${g.id}` : "/events", null, art, h("span", null, h("b", null, d.line), d.detail ? h("span", { class: "dim" }, d.detail) : null), h("time", { datetime: new Date(n.createdAt * 1000).toISOString(), class: "dim" }, ago(n.createdAt))));
          }),
        ),
      ),
    );
  }

  if (quest) blocks.push(questModule(quest));

  if (playing.length) blocks.push(module("continue", "Continue playing", null, strip(playing.map((x) => x.g), (g) => { const r = playing.find((x) => x.g.id === g.id)!.r; return `${ago(r.lastPlayedAt)} · ${fmtPlaytime(r.seconds)} played`; })));
  if (library.length) blocks.push(module("library", "Your library", link("/browse", { class: "acct-link" }, "Add games"), strip(library)));
  if (hearts.length) blocks.push(module("loved", "Games you love", null, strip(hearts)));
  if (creators.length)
    blocks.push(
      module(
        "following",
        "Creators you follow",
        null,
        h(
          "div",
          { class: "acct-chips" },
          creators.map((c) => link(`/c/${c.id}`, { class: "acct-chip" }, c.name)),
        ),
      ),
    );

  if (!playing.length && !library.length && !hearts.length && !creators.length)
    blocks.push(
      h(
        "section",
        { class: "acct-mod acct-empty acct-enter" },
        h("h2", null, "Your space fills up as you play"),
        h("p", { class: "dim" }, "Heart a game to keep it here, add games to your library in the app, and follow creators to hear when they ship. Games you play in the Banger app show up under Continue playing."),
        h("div", { class: "acct-row" }, link("/browse", { class: "btn" }, "Browse games"), link("/download", { class: "btn ghost" }, "Get the app")),
      ),
    );

  clear(mods, blocks);
  return root;
}

function questModule(q: QuestProgress): HTMLElement {
  return h(
    "section",
    { id: "haunt", class: `acct-mod acct-quest acct-enter${q.done ? " done" : ""}` },
    h(
      "div",
      null,
      h("p", { class: "kicker" }, "Open Haunt"),
      h("h2", null, q.done ? "Your pumpkin is lit" : "Light up your pumpkin"),
      h("p", { class: "dim" }, progressText(q)),
      h("ol", { class: "acct-pumpkins", "aria-label": `${Math.min(q.counted.length, q.needed)} of ${q.needed}` }, Array.from({ length: q.needed }, (_, i) => h("li", { class: i < q.counted.length || q.done ? "lit" : "" }, "🎃"))),
    ),
    link("/haunt", { class: "btn ghost" }, q.done ? "See Open Haunt" : "Find a haunt"),
  );
}
